/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { type SerializedStyles, jsx } from '@emotion/react';

import * as React from 'react';
import { Helmet } from 'react-helmet-async';

import { type ImgParams } from 'js/lib/imgix';

import type { ResponsiveImageType } from 'bundles/cds-labs/components/Modules/HeroBentoModule/ResponsiveImage.types';
import useResponsiveImageSources, {
  FETCH_PRIORITIES,
  type FetchPriority,
} from 'bundles/cds-labs/components/Modules/hooks/useResponsiveImageSources';

export type { ResponsiveImageType };

export type Props = {
  images: ResponsiveImageType[];
  fetchPriority: FetchPriority;
  imgParams?: ImgParams;
  /**
   * Applied to the rendered `<img>` — the element that actually paints. Use this to
   * control sizing (width/height/object-fit/etc.).
   */
  className?: string;
  /** Emotion styles applied to the wrapping `<picture>` — use for layout of the wrapper box itself. */
  pictureCss?: SerializedStyles | Array<SerializedStyles>;
};

/**
 * Renders a responsive image with the given images and fetch priority.
 * @param images - The images to render.
 * @param fetchPriority - The fetch priority to use for the image.
 * @param imgParams - Optional imgix parameters to customize image optimization (e.g., quality, fit).
 * These params are merged with the defaults, allowing override of defaults.
 * @returns A <picture> element with <source> elements for each image and a <img> element for the fallback image.
 * If fetchPriority is 'high', a <Helmet> element is also returned, which renders <link> elements
 * to preload the images based on the media queries to improve the loading performance.
 * @example
 * <ResponsiveImage
 *   images={[
 *     { url: 'https://example.com/mobile-image.jpg', width: 100, height: 100, media: '(max-width: 768px)' },
 *     { url: 'https://example.com/desktop-image.jpg', width: 200, height: 200, media: '(min-width: 769px)', isFallback: true },
 *   ]}
 *   fetchPriority="high"
 * />
 */
export const ResponsiveImage = ({ images, fetchPriority, imgParams, className, pictureCss }: Props) => {
  const { sources, fallback, preloadLinks } = useResponsiveImageSources(images, fetchPriority, imgParams);

  if (!fallback || !sources.length) {
    return null;
  }

  const imageOutput = (
    <picture css={pictureCss}>
      {sources.map(({ image, srcSet }) => (
        <source srcSet={srcSet} media={image.media} key={image.id} />
      ))}
      <img
        src={fallback.src}
        srcSet={fallback.srcSet}
        fetchPriority={fetchPriority}
        alt={fallback.image.alt ?? ''}
        className={className}
        key={fallback.image.id}
      />
    </picture>
  );

  if (fetchPriority !== FETCH_PRIORITIES.high) {
    return imageOutput;
  }

  return (
    <>
      <Helmet link={preloadLinks} />
      {imageOutput}
    </>
  );
};

export default ResponsiveImage;
